import { useState } from 'react';

import styled from 'styled-components';

import { MaxLengthCounter } from '@components/max-length-counter';
import { Stack } from '@components/stack';
import { TextField } from '@components/text-field';
import { pxToRem } from '@utils/converters';

import { EditableCellPopover } from './editable-cell-popover';
import { EditableCellEditorProps } from './types';
import { useEditableCellTrigger } from './use-editable-cell-trigger';

import { SimpleText, textColumnMaxLength } from '../renderers';

type EditableTextCellProps = EditableCellEditorProps<string | undefined>;

export const EditableTextCell = ({ column, row, value }: EditableTextCellProps) => {
  const getTrigger = useEditableCellTrigger();
  const [draft, setDraft] = useState(value ?? '');

  // typeRenderMap.text renders its own button - the one that opens the
  // long-text modal - so the trigger draws the value itself rather than
  // nesting a button inside the popover's.
  const trigger = getTrigger(value, column, row, () => <SimpleText text={value ?? ''} />);

  return (
    <EditableCellPopover
      trigger={trigger}
      column={column}
      row={row}
      value={value}
      onOpen={() => setDraft(value ?? '')}
      isSaveDisabled={draft.trim() === (value ?? '')}
      onSave={async () => {
        await column.onEdit?.(row, draft.trim());
      }}
    >
      <TextEditorBody>
        <Stack space={1}>
          <TextField
            label={column.title}
            value={draft}
            onChange={setDraft}
            maxChars={textColumnMaxLength}
            autoFocus
          />
          <MaxLengthCounter currentLength={draft.length} maxLength={textColumnMaxLength} />
        </Stack>
      </TextEditorBody>
    </EditableCellPopover>
  );
};

// A line of text needs more room than the 320px the one-value editors take -
// the designs give this field 424px, the width it has in a sidepanel.
const TextEditorBody = styled.div({ minWidth: pxToRem(424) });
